
import { Component, Input, ViewChild, ViewEncapsulation, Output, EventEmitter, OnInit, IterableDiffers } from '@angular/core';
import { FullCalendarComponent } from '@fullcalendar/angular';
import { TranslateService } from '@ngx-translate/core';
import { MatDrawer } from '@angular/material/sidenav';
import { Calendar, CalendarOptions } from '@fullcalendar/core';
import moment from 'moment';
import 'moment/locale/it';
import 'moment/locale/en-gb';
import 'moment/locale/fr';
import 'moment/locale/es';
import * as _ from 'lodash';
import interactionPlugin from '@fullcalendar/interaction';
import dayGridPlugin from '@fullcalendar/daygrid';

@Component({
  selector: 'is-calendar-widget',
  templateUrl: './is-calendar-widget.component.html',
  styleUrls: ['./is-calendar-widget.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class IsCalendarWidgetComponent implements OnInit {

  // PROPERTIES
  @Input() eventList = [];
  @Input() primaryColor: string = '#c9609f';
  @ViewChild('sidenavPanel') sidenavPanel: MatDrawer;
  @ViewChild('calendar') calendarComponent: FullCalendarComponent;
  @Output() dateChanges: EventEmitter<any> = new EventEmitter<any>();

  public calendar: Calendar;
  public clickedEvent: any = {};
  private _iterableDiffer: any;
  // public currentLanguage: string = '';

  // FULLCALENDAR PROPERTIES
  public calendarOptions: CalendarOptions = {
    headerToolbar: false,
    height: '100%',
    locale: 'en',
    firstDay: 1, // Setting Monday as a first day.
    plugins: [
      dayGridPlugin,
      interactionPlugin
    ],
    initialView: 'dayGridMonth',
    eventClick: (info) => {
      const newEvent = Object.assign((info.event), (info.event.extendedProps));
      if (_.isEqual(this.clickedEvent, newEvent)) {
        this.toggleSidenav();
      } else {
        this.toggleSidenav('open');
      }
      this.clickedEvent = newEvent;
    },
    eventColor: this.primaryColor,
    dayMaxEvents: 1
  };

  constructor(
    private _translate: TranslateService,
    private _iterableDiffers: IterableDiffers
  ) {
    _translate.onLangChange.subscribe((lang) => {
      this._setTranslation();
    });
    this._iterableDiffer = _iterableDiffers.find([]).create(null);
  }

  ngDoCheck() {
    const changes = this._iterableDiffer.diff(this.eventList);
    if (changes) {
      this.addEvents();
    }
  }

  ngOnInit() {
    this.calendarOptions['eventColor'] = this.primaryColor;
    this.calendarOptions['locale'] = this._translate.currentLang;

    this._setListeners();
    this._setTranslation();
  }

  private _setListeners(): void {
    window.addEventListener('sidenav-toggled', (event) => {
      setTimeout(() => {
        this.calendar.updateSize();
      }, 500);
    });
  }

  private _setTranslation(): void {
    this.calendarOptions['moreLinkText'] = this._translate.instant('view_all');
  }

  ngAfterViewInit(): void {
    this.addEvents();
  }

  private addEvents(): void {
    if (this.calendarComponent) {
      this.calendar = this.calendarComponent.getApi();
      this.calendar.removeAllEvents(); // PREVENT DUPLICATED EVENTS
      for (const event of this.eventList) {
        event['display'] = 'block';
        this.calendar.addEvent(event);
      }
    }
  }

  public toggleSidenav(status?: string): void {
    if (!status) {
      this.sidenavPanel.opened = !this.sidenavPanel.opened;
    } else {
      switch (status) {
        case 'open': { this.sidenavPanel.open(); } break;
        case 'close': { this.sidenavPanel.close(); } break;
      }
    }
    setTimeout(() => {
      this.calendar.updateSize();
    }, 500);
  }

  /**
    * CHANGE MONTH IN CALENDAR AND GET DATE FOR API
    * (ex. start:2021-01-01 end:2021-01-31)
    */
  public nextMonth(): void {
    this.calendar.next();
    const obj = {
      start: moment(this.calendar.getDate()).startOf('month')?.format('YYYY-MM-DD'),
      end: moment(this.calendar.getDate()).endOf('month').format('YYYY-MM-DD')
    };
    this.dateChanges.emit(obj);
  }

  /**
   * CHANGE MONTH IN CALENDAR AND GET DATE FOR API
   * (ex. start:2021-01-01 end:2021-01-31)
   */
  public beforeMonth(): void {
    this.calendar.prev();
    const obj = {
      start: moment(this.calendar.getDate()).startOf('month')?.format('YYYY-MM-DD'),
      end: moment(this.calendar.getDate()).endOf('month').format('YYYY-MM-DD')
    };
    this.dateChanges.emit(obj);
  }

  // OPEN CURRENT MONTH IN CALENDAR
  public today(): void {
    this.calendar.today();
    const obj = {
      start: moment(this.calendar.getDate()).startOf('month')?.format('YYYY-MM-DD'),
      end: moment(this.calendar.getDate()).endOf('month').format('YYYY-MM-DD')
    };
    this.dateChanges.emit(obj);
  }

  /** Returns the current date formatted */
  public getCalendarDate(): string {
    moment.locale(this._translate.currentLang || 'it');
    return moment(this.calendar?.getDate()).format('MMMM YYYY');
  }

  /** Check if the passed value is a link */
  public checkLink(link) {
    if (link === undefined) return false;
    return true;
  }
}
